import { supabase } from "@/integrations/supabase/client";
import { downloadFileName } from "@/components/workflows/docProvenance";


/**
 * Shape of a document row we can derive a storage path from.
 * We accept any subset — different callers select different columns.
 */
export interface DownloadableDoc {
  document_name?: string | null;
  file_path?: string | null;
  file_url?: string | null;
  signed_file_path?: string | null;
  signed_file_url?: string | null;
  original_file_url?: string | null;
}

/**
 * Parse a storage object path out of a stored URL.
 * Handles legacy `/object/public/documents/...` and `/object/sign/documents/...`
 * forms, as well as bare paths. Returns null if nothing usable can be extracted.
 */
export function parseDocumentPath(input?: string | null): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Strip supabase storage URL prefix if present
  const match = trimmed.match(/\/object\/(?:public|sign)\/documents\/(.+)$/);
  const raw = match ? match[1] : trimmed;

  // Drop any query string (signed URLs, download params)
  const noQuery = raw.split("?")[0];

  // Reject anything still looking like an http(s) URL — we could not parse it.
  if (/^https?:\/\//i.test(noQuery)) return null;

  return noQuery || null;
}

/**
 * Derive the best storage path for a document, preferring the signed version
 * when explicitly requested (and available).
 */
export function getDocumentStoragePath(
  doc: DownloadableDoc,
  opts: { signed?: boolean } = {},
): string | null {
  if (opts.signed) {
    return (
      doc.signed_file_path ||
      parseDocumentPath(doc.signed_file_url) ||
      doc.file_path ||
      parseDocumentPath(doc.file_url) ||
      null
    );
  }
  return (
    doc.file_path ||
    parseDocumentPath(doc.file_url) ||
    doc.signed_file_path ||
    parseDocumentPath(doc.signed_file_url) ||
    parseDocumentPath(doc.original_file_url) ||
    null
  );
}

function slugFilename(name: string | null | undefined, suffix = ""): string {
  // Underscore slugs with a date stamp, so every download reads as the
  // Association's own paper and sorts chronologically.
  return downloadFileName(name, suffix.replace(/^-/, ""));
}


/**
 * Generate a short-lived signed URL for a generated document. Prefers
 * `file_path` (or the signed variant), and always resolves at click time so
 * expired URLs never leak into the UI.
 *
 * Throws a human-readable Error on failure so callers can toast it.
 */
export async function getDocumentSignedUrl(
  doc: DownloadableDoc,
  opts: { signed?: boolean; expiresIn?: number; download?: boolean | string } = {},
): Promise<string> {
  const path = getDocumentStoragePath(doc, { signed: opts.signed });
  if (!path) {
    throw new Error("This document has no file attached yet.");
  }

  const filename =
    typeof opts.download === "string"
      ? opts.download
      : slugFilename(doc.document_name, opts.signed ? "-signed" : "");

  const { data, error } = await supabase.storage
    .from("documents")
    .createSignedUrl(path, opts.expiresIn ?? 300, {
      download: opts.download === false ? undefined : filename,
    });

  if (error || !data?.signedUrl) {
    console.error("createSignedUrl failed", { path, error });
    throw new Error(
      "Could not generate a download link. If this keeps happening, please contact support.",
    );
  }

  return data.signedUrl;
}

/**
 * Trigger a browser download of a document by fetching the signed URL as a
 * blob (so browsers actually save it instead of navigating to it).
 */
export async function downloadDocument(
  doc: DownloadableDoc,
  opts: { signed?: boolean; filename?: string } = {},
): Promise<void> {
  const url = await getDocumentSignedUrl(doc, {
    ...opts,
    download: opts.filename ?? undefined,
  });
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error("Failed to fetch the document.");
  const blob = await res.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const link = window.document.createElement("a");
  link.href = blobUrl;
  link.download =
    opts.filename ?? slugFilename(doc.document_name, opts.signed ? "-signed" : "");
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}

/** True when the document has any derivable storage path. */
export function hasDownloadablePath(doc: DownloadableDoc): boolean {
  return getDocumentStoragePath(doc) !== null;
}
